import CodingalOffering from '@components/kids-teens-coding-camps/codingal-offering'
import Courses from '@components/kids-teens-coding-camps/courses'
import FirstFold from '@components/kids-teens-coding-camps/first-fold'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import Footer from '@components/common/footer'
import NavBar from '@components/common/navbar'
import Articles from '@components/home/articles'
import Faq from '@components/home/faq'
import MoneyBackGuarantee from '@components/home/guarantee'
import Teachers from '@components/home/teachers'
import Partners from '@components/locale-pages/home/us/partners'
import { FAQ_CONTENTS, VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { FAQPageJsonLd, NextSeo } from 'next-seo'
import { formatSeoConfig } from '@lib/seo'
import date from '@lib/utils/date.utils'
import { Trans } from '@lingui/macro'
import TextImageCommonFold from '@components/common/text-image-common-fold'
import { Investors } from '@components/book-trial-class-jp/investors'

const SummerCodingCamp = formatSeoConfig({
  title: `Summer coding camp for kids and teens | Codingal`,
  description: `Online coding summer camp for kids and teens for ages 7-18. Explore scratch, python, web development, data science, roblox, & more with the virtual online class `,
  url: 'https://www.codingal.com/kids-teens-coding-camps/summer/',
  metaImage: '/images/coding-summer-camp/summer-camp-og.jpg',
})
const couponCode = 'SUMMER50'

const CodingSummerCamp = () => {
  return (
    <>
      <NextSeo {...SummerCodingCamp} />
      <FAQPageJsonLd
        mainEntity={FAQ_CONTENTS.map(
          ({ acceptedAnswerTextString, questionName }) => ({
            acceptedAnswerText: acceptedAnswerTextString,
            questionName: questionName,
          })
        )}
      />
      <NavBar isSticky />
      <FirstFold
        title={
          <>
            <Trans>Summer Coding Camp for Kids & Teens</Trans> {date().year()}
          </>
        }
        description={
          <>
            <p className="lg:w-3/5 text-center lg:text-left text-sm lg:text-base">
              <Trans>
                Empower your child with essential coding skills at our Summer
                Coding Camp, specifically designed for kids and teens. Dive into
                the exciting world of technology with hands-on projects in
                Scratch, Python, Web & App Development, Roblox, and Data
                Science.
              </Trans>
            </p>
            <p className="lg:w-3/5 text-center lg:text-left pb-2 text-sm lg:text-base">
              <Trans>
                Our expert instructors ensure a comprehensive learning
                experience. Each session is designed to boost creativity,
                problem-solving skills, and technical knowledge. Plus, every
                student will receive a certificate upon completion, celebrating
                their achievements and new skills. <br />
                Register today and watch your child thrive in the digital age!
              </Trans>
            </p>
          </>
        }
        couponCode={couponCode}
        coursePrice={149}
        discountedPrice={75}
        offerPercentage={50}
        imagePath="/images/coding-summer-camp/course-image"
        imageAlt="Code, Build and Hack with coding camp for kids and teens"
        backgroundColour="#18244f"
      />
      <Partners />
      <Courses
        couponCode={couponCode}
        title={
          <Trans>
            Choose from personalized Summer Coding Camps for your child
          </Trans>
        }
      />
      <VideoTestimonial
        title="Hear from parents and students how our Coding course changed their lives"
        video_testimonial={VIDEO_TESTIMONIAL}
      />
      <TrustpilotReview />
      <Teachers
        isShowBecomeTeacher={false}
        title="Learn coding from the best IT instructors"
        ctaLink="#courses"
        ctaText="Claim your spot now"
      />
      <CodingalOffering
        title={<Trans>Exclusive advantages of Summer Coding Camp</Trans>}
      />
      <MoneyBackGuarantee isCodingCampPage />
      <TextImageCommonFold
        title={
          <>
            A summer school program with{' '}
            <span className="yellow-underline">
              fun activities and lessons for kids
            </span>
          </>
        }
        description={
          <>
            <p>
              Looking for an exciting and educational summer camp for kids? Join
              our AI & Coding Summer Camp, where fun meets future-ready
              learning!
            </p>
            <p>
              Perfect for kids and teens, this hands-on program dives into
              coding, AI, and STEM through engaging projects like creating games
              in Scratch, learning Python, building apps, and exploring Data
              Science and Roblox.
            </p>
            <p>
              Led by expert instructors, your child will develop technical
              skills, boost creativity, and gain confidence. Participants will
              also earn a STEM-accredited certificate of completion to celebrate
              their achievements.
            </p>
            <p>
              With affordable pricing and discounts, this camp promises a summer
              of discovery and innovation. Enroll today to secure your spot!
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-activities"
        imageWidth={370}
        imageHeight={373}
      />
      <TextImageCommonFold
        title={
          <>
            STEM.org Accredited{' '}
            <span className="yellow-underline">Educational Experience</span> and
            Certificates
          </>
        }
        description={
          <>
            <p>
              Codingal offers a STEM.org-accredited curriculum that equips kids
              and teens with essential coding skills. Designed to be fun and
              engaging, our courses build a strong foundation in coding while
              enhancing creativity, problem-solving, and critical thinking. From
              beginners to advanced learners, students explore exciting projects
              that make learning enjoyable.
            </p>
            <p>
              With STEM.org certifications, your child’s accomplishments are
              recognized, showcasing their technical expertise and boosting
              confidence to tackle real-world challenges. Our accredited
              programs prepare young minds for academic and career success,
              empowering them with future-ready skills.
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-certificate"
        imageWidth={370}
        imageHeight={370}
      />
      <TextImageCommonFold
        title={
          <>
            Unlock your{' '}
            <span className="yellow-underline">
              skills and earn coding certifications
            </span>
          </>
        }
        description={
          <>
            <p>
              Empower your child this summer with Codingal’s immersive coding
              courses, designed to make learning fun and transformative. Through
              hands-on, project-based lessons, students create real-world
              projects like games in Scratch, Python programs, and data science
              applications while enhancing creativity and critical thinking.
            </p>
            <p>
              Upon completion, students earn a STEM.org certification—a
              prestigious recognition of their skills and achievements. These
              certifications showcase problem-solving abilities and technical
              expertise, paving the way for success in app development, game
              creation, and even artificial intelligence.
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-certificate-2"
        imageWidth={370}
        imageHeight={370}
      />
      <TextImageCommonFold
        title={
          <>
            The best summer camp for{' '}
            <span className="yellow-underline">
              kids to learn coding basics and fundamentals
            </span>
          </>
        }
        description={
          <>
            <p>
              Our Summer Coding Camp is the ultimate adventure for kids to dive
              into the world of coding—where fun meets learning!
            </p>
            <p>
              With hands-on projects, interactive lessons, and a focus on core
              coding concepts, your child will master coding languages like
              Scratch, Python, and more. Whether they’re creating their very
              first video game or building their own website, our camp turns
              coding into an exhilarating and rewarding journey.
            </p>
            <p>
              Choose from over 12 exciting programs like Scratch Explorer,
              Scratch Champion, Python Foundations, Python Champion, Website
              Wizard, Website Champion, App Inventor, App Champion, Roblox
              Rookie, Roblox Champion, Data Science Intro, and Data Science
              Champion. With six lessons per program, your child will have the
              chance to explore their favorite topics and excel through
              project-based learning.
            </p>
            <p>
              Each program comes with a certification, so kids can proudly
              showcase their new skills. And the best part? They’ll learn at the
              perfect pace—challenging yet fun—making this summer the ideal time
              to build their coding future!
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-student-learning"
        imageWidth={370}
        imageHeight={373}
      />

      <TextImageCommonFold
        title={
          <>
            Our summer courses are appropriate for{' '}
            <span className="yellow-underline">
              high school, middle school,
            </span>{' '}
            and elementary school students
          </>
        }
        description={
          <>
            <p>
              Our summer coding programs support students aged 6-18, offering
              specialized courses tailored to every grade level.
            </p>
            <p>
              Whether your child is just beginning to explore the world of
              coding or is ready to tackle more advanced topics, our courses are
              designed for grades K-12. The curriculum is thoughtfully
              structured with age-appropriate lessons, ensuring that kids
              develop the right skills at each stage—whether mastering the
              basics with Scratch or advancing to languages like Python and
              JavaScript.
            </p>
          </>
        }
        imagePath="/images/coding-camp/summer-camp-school-kids"
        imageWidth={370}
        imageHeight={373}
      />

      <Faq
        title="Got questions? Find answers in our FAQ section"
        faqContents={FAQ_CONTENTS}
      />
      <Articles />
      <Investors isCodingCampPage />
      <Footer />
    </>
  )
}

export default CodingSummerCamp
